import Phaser from 'phaser';
import * as C from 'tower/constants';
import {
  Direction,
  State,
  StateMachine,
  WaitState,
  asyncAnimation,
  asyncTween,
  gridMove,
  randomChoice,
  wait,
  weightedChoice,
} from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import FlashableSprite from 'tower/flashable';
import { HitOptions } from 'tower/types';
import Projectile from './enemy/projectile';
import Room from './room';

export default class Boss extends FlashableSprite {
  eyeStateMachine: StateMachine<State>;
  movingStateMachine: StateMachine<State>;

  hp = 3;
  touchDamage = 2;
  enemyOptions = {
    solid: true,
  };

  fireballTimer: Phaser.Time.TimerEvent;
  yellTimer: Phaser.Time.TimerEvent;
  yell: Phaser.Sound.BaseSound;
  hurt: Phaser.Sound.BaseSound;

  declare body: Phaser.Physics.Arcade.Body;

  constructor(
    public scene: BattleScene,
    public room: Room,
    x: number,
    y: number,
  ) {
    super(scene, x, y, 'boss', 0);
    this.setDepth(C.DEPTHS.Enemies);
    this.play('bossWalk[closed]');

    this.movingStateMachine = new StateMachine('strafing', {
      strafing: new StrafingState(scene, this),
      ramming: new RammingState(scene, this),
    });

    this.eyeStateMachine = new StateMachine('closed', {
      closed: new WaitState([400, 700], 'opening'),
      opening: new EyeOpeningState(scene, this),
      open: new WaitState([150, 200], 'closing'),
      closing: new EyeClosingState(scene, this),
    });

    scene.add.existing(this);
    scene.physics.add.existing(this);
    room.enemies.add(this);

    this.yell = scene.sound.add('bossyell');
    this.hurt = scene.sound.add('bosshurt');

    this.yellTimer = scene.time.addEvent({
      delay: 2500,
      loop: true,
      paused: true,
      callback: () => this.yell.play(),
    });

    this.fireballTimer = scene.time.addEvent({
      delay: 1000,
      loop: true,
      paused: true,
      callback: async () => {
        const fireball = new Projectile(scene, this.x, this.y, 'fireball', Direction.Down, { target: 'player' });
        fireball.body.setVelocity(0);
        await wait(scene, 200);
        scene.physics.moveToObject(fireball, scene.player, C.FIREBALL_SPEED);
      },
    });

    this.body.setImmovable(true);
  }

  async hit(options: HitOptions) {
    // To hit:
    // - Eye must be open
    // - Must be an arrow
    // - Must be hitting eye (i.e. within the center of our hitbox on the x axis)
    const weaponBody = options.weaponBody;
    if (
      this.eyeStateMachine.state !== 'open' ||
      options.weaponType !== 'arrow' ||
      weaponBody.x < this.x - 8 ||
      weaponBody.x > this.x + 8
    ) {
      this.scene.sound.play('deflectSmall');
      return;
    }

    if (--this.hp <= 0) {
      const explosion = this.scene.add
        .sprite(this.x, this.y, 'enemies', 11)
        .setDepth(C.DEPTHS.Enemies)
        .play('enemyDeath')
        .once('animationcomplete', () => explosion.destroy());
      this.hurt.play();
      this.emit('death');
      this.destroy();
    } else {
      // Hurt
      this.flash(6);
      this.hurt.play();
      this.yell.stop();
      this.eyeStateMachine.transition('closing');
    }
  }

  update() {
    if (this.isDestroyed) {
      return;
    }

    if (this.yellTimer.paused) {
      this.yellTimer.paused = false;
    }
    if (this.fireballTimer.paused) {
      this.fireballTimer.paused = false;
    }

    this.eyeStateMachine.update();
    this.movingStateMachine.update();
  }

  destroy(fromScene?: boolean) {
    this.fireballTimer.destroy();
    this.yellTimer.destroy();
    super.destroy(fromScene);
  }

  static preload(scene: Phaser.Scene) {
    scene.load.spritesheet('boss', 'img/boss.png', { frameWidth: 48, frameHeight: 16 });
    scene.load.audio('bossyell', 'audio/bossyell.mp3');
    scene.load.audio('bosshurt', 'audio/bosshurt.mp3');
  }

  static create(scene: BattleScene) {
    scene.anims.create({
      key: 'bossWalk[closed]',
      frameRate: 4,
      frames: scene.anims.generateFrameNumbers('boss', {
        frames: [0, 1, 2, 3],
      }),
      repeat: -1,
    });
    scene.anims.create({
      key: 'bossOpenEye',
      frameRate: 4,
      frames: scene.anims.generateFrameNumbers('boss', {
        frames: [4, 5],
      }),
      repeat: 0,
    });
    scene.anims.create({
      key: 'bossCloseEye',
      frameRate: 4,
      frames: scene.anims.generateFrameNumbers('boss', {
        frames: [5, 4],
      }),
      repeat: 0,
    });
    scene.anims.create({
      key: 'bossWalk[open]',
      frameRate: 4,
      frames: scene.anims.generateFrameNumbers('boss', {
        frames: [6, 7],
      }),
      repeat: -1,
    });
  }
}

class BossState extends State {
  constructor(
    protected scene: BattleScene,
    protected boss: Boss,
  ) {
    super();
  }
}

class StrafingState extends BossState {
  async handleEntered() {
    const { boss, scene } = this;

    let direction = randomChoice([Direction.Left, Direction.Right]);
    if (boss.x < boss.room.x + 128) {
      direction = Direction.Right;
    } else if (boss.x > boss.room.x + 288) {
      direction = Direction.Left;
    }

    await asyncTween(scene, {
      targets: [boss],
      x: gridMove(boss.x, boss.y, direction, 24)[0],
      duration: 1000,
    });

    return this.transition(
      weightedChoice([
        ['strafing', 2],
        ['ramming', 1],
      ]),
    );
  }
}

class RammingState extends BossState {
  async handleEntered() {
    const { boss, scene } = this;

    await asyncTween(scene, {
      targets: [boss],
      y: '+=72',
      duration: 2000,
    });

    await asyncTween(scene, {
      targets: [boss],
      y: '-=72',
      duration: 2000,
    });

    return this.transition(
      weightedChoice([
        ['strafing', 3],
        ['ramming', 1],
      ]),
    );
  }
}

class EyeOpeningState extends BossState {
  async handleEntered() {
    await asyncAnimation(this.boss, 'bossOpenEye');
    this.boss.play('bossWalk[open]');
    return this.transition('open');
  }
}

class EyeClosingState extends BossState {
  async handleEntered() {
    await asyncAnimation(this.boss, 'bossCloseEye');
    this.boss.play('bossWalk[closed]');
    return this.transition('closed');
  }
}
